import React, { useCallback, useEffect, useContext, useState } from "react";
import { useFormik } from "formik";
import { Button, Col, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import ButtonSubmitFormikPermission from "../../../components/UI/Button/ButtonSubmitFormikPermission";
import FormAlert from "../../../components/UI/FormAlert/FormAlert";
import OverlayInfoAdicional from "../../../components/UI/Overlays/OverlayInfoAdicional";
import TextInputFormik from "../../../components/UI/TextInput/TextInputFormik";
import TextSpinner from "../../../components/UI/Spinner/TextSpinner";
import { AuthContext } from "../../../context/AuthContext";
import funcionalidades from "../../../context/AuthContext.funcionalidades";
import useApiClient, { apiRoutes } from "../../../hooks/apiClient";

const validationSchema = Yup.object().shape({
  codigo: Yup.string().default(""),
  nome: Yup.string()
    .ensure()
    .default("")
    .required("Informe o Nome da Fase")
    .max(200, "A descrição não pode ter mais de 200 caracteres"),
  descricao: Yup.string()
    .ensure()
    .default("")
    .required("Informe a Descrição da Fase")
    .max(4000, "A descrição não pode ter mais de 4000 caracteres"),
});

const initialValues = {};
validationSchema._nodes.forEach((key) => {
  initialValues[key] = "";
});

const FormFase = ({ history, location }) => {
  const { state } = location;
  const { isAllowed } = useContext(AuthContext);
  const [dadosInfo, setDadosInfo] = useState({});

  // Formik
  const { request: submitRequest, data: submitData, errors: submitErrors, isLoading: submitIsLoading } = useApiClient();

  const handleSubmit = useCallback(
    (values) => {
      if (values.codigo) submitRequest(apiRoutes.fiscalizacao.parametrizacao.alteraFase, values);
      else submitRequest(apiRoutes.fiscalizacao.parametrizacao.incluiFase, values);
    },
    [submitRequest],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const { setSubmitting, setErrors, isSubmitting, isValid } = formik;

  useEffect(() => {
    if ((submitData || submitErrors) && isSubmitting && !submitIsLoading) formik.setSubmitting(false);

    if (submitErrors && isValid && !submitIsLoading) formik.setErrors({ ...formik.errors, ...submitErrors });
  }, [formik, setSubmitting, setErrors, isSubmitting, isValid, submitData, submitErrors, submitIsLoading]);

  useEffect(() => {
    if (!submitData && !submitErrors) return;

    if (submitData) {
      toast.success("Fase salva com sucesso");
      history.push("/app/fase");
    }
  }, [submitData, submitErrors, history]);

  // Carregar item
  const { request: faseRequest, data: faseData, errors: faseErrors, isLoading: faseIsLoading } = useApiClient();

  useEffect(() => {
    if (state && state.itemKey) {
      // setSubmitting(true);
      faseRequest(apiRoutes.fiscalizacao.parametrizacao.consultaFase, { id: state.itemKey });
      delete state.itemKey;
    }
  }, [faseRequest, state]);

  useEffect(() => {
    if ((!faseData && !faseErrors) || faseIsLoading) return;

    if (!formik.values.codigo && faseData && faseData.result.length) {
      const info = {
        codigo: faseData?.result[0]?.codigo,
        data: {
          documentoInclusao: faseData?.result[0]?.usuarioInclusao,
          nomeFormalInclusao: faseData?.result[0]?.nomeFormalInclusao,
          nomeFormalAlteracao: faseData?.result[0]?.nomeFormalAlteracao,
          documentoAlteracao: faseData?.result[0]?.usuarioAlteracao,
          dataInclusao: faseData?.result[0]?.dataAlteracao,
          dataAlteracao: faseData?.result[0]?.dataInclusao,
        },
      };

      formik.setValues({ ...faseData.result[0] });
      setDadosInfo(info);
    }

    if (faseErrors) {
      toast.error(faseErrors.message);
    }
  }, [formik, faseData, faseErrors, faseIsLoading]);

  return (
    <>
      <h1 className="h3 mb-4 text-gray-800">Fases da Ação Fiscal</h1>
      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <Row>
                <Col sm="11">
                  <h6 className="m-0 font-weight-bold text-primary flex-grow-1">
                    {formik.values.codigo ? "Alteração de Fase" : "Nova Fase"}
                  </h6>
                </Col>
                <Col sm="1" className="text-right">
                  <OverlayInfoAdicional data={dadosInfo} keyFieldName="codigo" />
                </Col>
              </Row>

              {/* <Button href="#" className="btn btn-primary btn-sm btn-user">Botão</Button> */}
            </div>
            <div className="card-body">
              <FormAlert
                formik={formik}
                data={submitData}
                errors={submitErrors}
                isLoading={submitIsLoading || faseIsLoading}
              />
              <Form noValidate onSubmit={formik.handleSubmit}>
                <fieldset
                  disabled={
                    formik.values.codigo
                      ? !isAllowed(funcionalidades.fase.alterar)
                      : !isAllowed(funcionalidades.fase.inserir)
                  }
                >
                  <Row>
                    <Form.Group as={Col} sm={12} md={4}>
                      <Form.Label>Nome</Form.Label>
                      <TextInputFormik formik={formik} field="nome" maxLength="200" />
                    </Form.Group>
                    <Form.Group as={Col} sm={12} md={8}>
                      <Form.Label>Descrição</Form.Label>
                      <TextInputFormik formik={formik} field="descricao" maxLength="4000" />
                    </Form.Group>
                  </Row>
                </fieldset>
                <Row className="mt-2">
                  <Col>
                    {formik.isSubmitting ? (
                      <TextSpinner text="Registrando fase..." />
                    ) : (
                      <ButtonSubmitFormikPermission
                        permission={formik.values.codigo ? funcionalidades.fase.alterar : funcionalidades.fase.inserir}
                        disabled={formik.isSubmitting}
                        name="Salvar"
                      >
                        Salvar
                      </ButtonSubmitFormikPermission>
                    )}
                    {"    "}
                    {!formik.isSubmitting && !formik.values.codigo && (
                      <Button variant="light" className="mr-3" onClick={formik.handleReset}>
                        Limpar
                      </Button>
                    )}
                    {"    "}
                    {!formik.isSubmitting && (
                      <Button variant="danger" onClick={() => history.goBack()}>
                        Cancelar
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FormFase;
